import React, { useEffect, useState } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import Button from '../components/Button';
import SEO from '../components/SEO';

const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: '40vh',
  minHeight: '300px',
  color: 'white',
  textAlign: 'center',
  overflow: 'hidden',
  background: 'linear-gradient(45deg, #3c005a 30%, #5c0080 90%)',
  padding: theme.spacing(8, 0),
}));

const BannerImage = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  transition: 'opacity 1s ease-in-out',
}));

const Overlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(4),
}));

const servicesStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Thrana Pharmacovigilance Services',
  url: 'https://www.thranatech.com/services',
  description:
    'Thrana provides end-to-end pharmacovigilance system setup, safety database provisioning, QPPV services, SDEA management, and audit support.',
  provider: {
    '@type': 'Organization',
    name: 'Thranatech.com',
    url: 'https://www.thranatech.com',
  },
};

const Services = () => {
  const [currentBanner, setCurrentBanner] = useState(0);

  const banners = [
    '/banner1.jpg',
    '/banner2.jpg',
    '/services banner1.jpg',
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % banners.length);
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  return (
    <>
      <SEO
        title="Thrana Services | Pharmacovigilance System Setup & Safety Operations"
        description="Discover Thrana's pharmacovigilance services: system setup, safety database provisioning, QPPV, SDEA management, audits, data migration, and literature translation."
        path="/services"
        structuredData={servicesStructuredData}
      />
      <Box>
      <HeroSection>
        {banners.map((banner, index) => (
          <BannerImage
            key={index}
            sx={{
              backgroundImage: `url(${banner})`,
              opacity: currentBanner === index ? 1 : 0,
            }}
          />
        ))}
        <Overlay>
          <Container maxWidth="md">
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{
                textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                fontWeight: 'bold',
                color: 'white',
              }}
            >
              Our Services
            </Typography>
            <Typography
              variant="h6"
              paragraph
              sx={{
                textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                color: 'white',
              }}
            >
              Comprehensive Pharmacovigilance Solutions
            </Typography>
          </Container>
        </Overlay>
      </HeroSection>

      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom sx={{ color: '#3c005a' }}>
          Services Menu Updated
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Detailed service offerings are now accessible via the Services navigation menu. Please use the dropdown to
          explore each service individually or reach out to our team for a tailored consultation.
        </Typography>
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Button component={Link} to="/services/pharmacovigilance-system-setup" variant="outlined" size="large">
            Browse Services
          </Button>
          <Button component={Link} to="/contact" size="large">
            Contact Us
          </Button>
        </Box>
      </Container>

      <Box sx={{ bgcolor: 'grey.100', py: 8 }}>
        <Container maxWidth="md">
          <Typography variant="h4" align="center" gutterBottom sx={{ color: '#3c005a' }}>
            Why Choose Our Services?
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#3c005a' }}>
                  Expert Team
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Our team consists of experienced pharmacovigilance professionals, regulatory experts, 
                  and technical specialists with deep industry knowledge.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#3c005a' }}>
                  Proven Track Record
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Successfully delivered services to pharmaceutical companies worldwide, 
                  ensuring compliance and operational excellence.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#3c005a' }}>
                  Customized Solutions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tailored services to meet your specific needs, whether you're a startup 
                  or an established pharmaceutical company.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#3c005a' }}>
                  Ongoing Support
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Continuous support and maintenance to ensure your systems remain up-to-date 
                  and compliant with evolving regulations.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom sx={{ color: '#3c005a' }}>
          Ready to Get Started?
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Contact us to discuss your specific needs and learn how our services can help your organization.
        </Typography>
        <Box sx={{ mt: 4 }}>
          <Button
            component={Link}
            to="/contact"
            size="large"
          >
            Contact Us
          </Button>
        </Box>
      </Container>
      </Box>
    </>
  );
};

export default Services;

